import { useEffect, useMemo, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { toast } from "react-toastify"
import { selectUser } from "../../../core/data/redux/slice/user"
import { isTeacher } from "../../../utils/rbacHelper"
import { selectBranches, selectCurrentBranch, type Branch } from "../../../core/data/redux/slice/branchesSlice"
import { campusBrandName, absoluteCampusLogoUrl } from "../../../utils/campusLogos"
import { getAllSessions, type Session } from "../../../service/sessions"
import { getAllTerms, type Term } from "../../../service/term-management"
import { getAllResultBreakdowns, type ResultBreakdown } from "../../../service/result-breakdown"
import { getAllClasses } from "../../../service/classes"
import { listGroups } from "../../../service/group"
import {
  listMarksSubjects,
  getMarksGrid,
  saveMarksGrid,
  submitMarksGrid,
  unsubmitMarksGrid,
  saveExamMeta,
  type MarksSubject,
  type MarksGrid as MarksGridData,
  type SaveMarksEntry,
} from "../../../service/marks-entry"
import { getTeacherWorkItems, submitMarks as submitTeacherMarks, type TeacherWorkItem } from "../../../service/teacher-portal"

// ── Special-marker helpers (verbatim RMS semantics) ─────────────────
// "A"/"AB" → absent (0, but max stays in denominator).
// "NA"/"N/A" → not-assessed (excluded from numerator AND denominator).
const isAbsentMarker = (v: any) => {
  if (v == null || v === "") return false
  const s = String(v).trim().toUpperCase()
  return s === "A" || s === "AB"
}
const isNaMarker = (v: any) => {
  if (v == null || v === "") return false
  return String(v).trim().toUpperCase().replace("/", "") === "NA"
}

const toId = (v: any): string => {
  if (!v) return ""
  if (typeof v === "object") return String(v._id || v.id || "")
  return String(v)
}

type SessionOpt = { _id: string; title: string; branch?: any }
type SectionOpt = { _id: string; name: string }
type ClassOpt = { _id: string; name: string; sections: SectionOpt[]; system?: string }
type GroupOpt = { _id: string; name: string; system: string; classes: string[] }

// ── Small confirm modal (no native window.confirm anywhere on this page) ──
type ConfirmState = { title: string; message: string; confirmLabel?: string; onConfirm: () => void }
const ConfirmModal = ({ confirm, onClose }: { confirm: ConfirmState | null; onClose: () => void }) => {
  if (!confirm) return null
  return (
    <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.5)", zIndex: 1060 }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header border-0 pb-0">
            <h5 className="modal-title">{confirm.title}</h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            <p className="mb-0">{confirm.message}</p>
          </div>
          <div className="modal-footer border-0 d-flex gap-2 justify-content-end">
            <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button
              className="btn btn-primary"
              onClick={() => { confirm.onConfirm(); onClose() }}
            >
              {confirm.confirmLabel || "Confirm"}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// One page, role-aware: a teacher gets a self-service view scoped to their
// own TeacherAssignment rows; everyone else gets the full admin cascade.
// Was two separate pages/routes until merged -- see TeacherMarksEntry below.
const MarksEntry = () => {
  const user = useSelector(selectUser)
  return isTeacher(user) ? <TeacherMarksEntry /> : <AdminMarksEntry />
}

// ── Teacher self-service view ───────────────────────────────────────
const TeacherMarksEntry = () => {
  const [items, setItems] = useState<TeacherWorkItem[]>([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState<TeacherWorkItem | null>(null)

  // A teacher's full assignment list can span every class/subject they
  // teach, all at once, with no way to narrow it. Class + Exam Term filters,
  // both default to "all" so nothing is hidden until the teacher chooses to.
  const [filterClass, setFilterClass] = useState("")
  const [filterTerm, setFilterTerm] = useState("")
  const [collapsedClasses, setCollapsedClasses] = useState<Record<string, boolean>>({})

  useEffect(() => {
    getTeacherWorkItems()
      .then((r) => setItems(r.data.items))
      .catch(() => toast.error("Failed to load work items"))
      .finally(() => setLoading(false))
  }, [])

  // Options derived from the teacher's own real assignments -- never a
  // separate "every class in the system" fetch, so the filter can only ever
  // offer classes/terms this teacher actually has something in.
  const classOptions = useMemo(() => {
    const seen = new Map<string, { id: string; name: string; order: number; system: string }>()
    for (const it of items) {
      const id = it.class_id || it.class_name
      if (!seen.has(id))
        seen.set(id, { id, name: it.class_name, order: it.class_order ?? 999, system: it.system_name || "" })
    }
    return Array.from(seen.values()).sort((a, b) => a.order - b.order)
  }, [items])

  const termOptions = useMemo(() => {
    const seen = new Map<string, string>()
    for (const it of items) {
      const id = it.exam_term_id || it.breakdown_title
      if (!seen.has(id)) seen.set(id, it.exam_term_name || it.breakdown_title || "Exam")
    }
    return Array.from(seen.entries())
  }, [items])

  const filteredItems = useMemo(
    () =>
      items.filter((it) => {
        if (filterClass && (it.class_id || it.class_name) !== filterClass) return false
        if (filterTerm && (it.exam_term_id || it.breakdown_title) !== filterTerm) return false
        return true
      }),
    [items, filterClass, filterTerm]
  )

  // Grouped by class for display, preserving the backend's already-correct
  // teaching-order sort.
  const groupedByClass = useMemo(() => {
    const groups: { key: string; className: string; systemName: string; items: TeacherWorkItem[] }[] = []
    const indexByKey = new Map<string, number>()
    for (const it of filteredItems) {
      const key = it.class_id || it.class_name
      if (!indexByKey.has(key)) {
        indexByKey.set(key, groups.length)
        groups.push({ key, className: it.class_name, systemName: it.system_name || "", items: [] })
      }
      groups[indexByKey.get(key)!].items.push(it)
    }
    return groups
  }, [filteredItems])

  const toggleClassCollapsed = (key: string) =>
    setCollapsedClasses((c) => ({ ...c, [key]: !c[key] }))

  const selectItem = (item: TeacherWorkItem) => {
    if (!item.course_structure_id) return
    if (!item.breakdown_id) {
      toast.error("No result breakdown linked to this assignment. Contact admin.")
      return
    }
    setSelected(item)
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="mb-3">
          <h3 className="page-title mb-1">Marks Entry</h3>
          <p className="text-muted mb-0">
            Pick a class · section · subject from your assignments to enter marks.
          </p>
        </div>

        {loading ? (
          <p className="text-muted">Loading assignments…</p>
        ) : items.length === 0 ? (
          <div className="card">
            <div className="card-body text-center py-5 text-muted">
              No mark-entry assignments. Contact admin to assign you to class · section · subject
              combinations.
            </div>
          </div>
        ) : (
          <div className="row g-3">
            {/* Work-item list */}
            <div className="col-md-4">
              <div className="card">
                <div className="card-header">
                  <h6 className="card-title mb-0">My assignments</h6>
                  <div className="d-flex gap-2 mt-2">
                    <select
                      className="form-select form-select-sm"
                      value={filterClass}
                      onChange={(e) => setFilterClass(e.target.value)}
                    >
                      <option value="">All classes</option>
                      {classOptions.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}{c.system ? ` · ${c.system}` : ""}
                        </option>
                      ))}
                    </select>
                    <select
                      className="form-select form-select-sm"
                      value={filterTerm}
                      onChange={(e) => setFilterTerm(e.target.value)}
                    >
                      <option value="">All exam terms</option>
                      {termOptions.map(([id, name]) => (
                        <option key={id} value={id}>{name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {filteredItems.length === 0 ? (
                  <div className="card-body text-center py-4 text-muted small">
                    No assignments match this filter.
                  </div>
                ) : (
                  groupedByClass.map((group) => {
                    const isCollapsed = collapsedClasses[group.key]
                    return (
                      <div key={group.key}>
                        <button
                          type="button"
                          className="list-group-item bg-light fw-semibold small d-flex align-items-center justify-content-between w-100 border-0 text-start py-2"
                          onClick={() => toggleClassCollapsed(group.key)}
                        >
                          <span>
                            {group.className}
                            {group.systemName && (
                              <span className="fw-normal text-muted"> · {group.systemName}</span>
                            )}
                          </span>
                          <span className="d-flex align-items-center">
                            <span className="badge bg-secondary-transparent text-secondary" style={{ marginRight: "10px" }}>{group.items.length}</span>
                            <i className={`ti ti-chevron-${isCollapsed ? "right" : "down"} text-muted`} />
                          </span>
                        </button>
                        {!isCollapsed && (
                          <div className="list-group list-group-flush">
                            {group.items.map((it) => (
                              <button
                                key={`${it.course_structure_id}::${it.exam_term_id}`}
                                className={`list-group-item list-group-item-action text-start py-3 ${
                                  selected?.course_structure_id === it.course_structure_id ? "active" : ""
                                }`}
                                onClick={() => selectItem(it)}
                              >
                                <div className="fw-semibold">
                                  {it.subject_name}
                                  {it.section_name && (
                                    // text-muted is a fixed gray -- stays gray even once Bootstrap
                                    // turns this row's own text white on .active (selected), making
                                    // it unreadable against the blue background. Dimming via opacity
                                    // on the inherited colour instead keeps it readable in both states.
                                    <span className="fw-normal" style={{ opacity: 0.75 }}> · Section {it.section_name}</span>
                                  )}
                                </div>
                                <div className="d-flex align-items-center gap-2 mt-2">
                                  <span
                                    className={`badge ${
                                      it.complete
                                        ? "bg-success-transparent text-success"
                                        : "bg-warning-transparent text-warning"
                                    }`}
                                  >
                                    {it.slip_status}
                                  </span>
                                  {it.is_final && (
                                    <span className="badge bg-info-transparent text-info">Final</span>
                                  )}
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )
                  })
                )}
              </div>
            </div>

            {/* Grid */}
            <div className="col-md-8">
              {!selected && (
                <div className="card">
                  <div className="card-body text-center py-5">
                    <i className="ti ti-click text-muted" style={{ fontSize: "2.5rem" }} />
                    <h6 className="mt-3 mb-1">No assignment selected</h6>
                    <p className="text-muted small mb-0">
                      Pick a class and subject from the list on the left to load its marks grid.
                    </p>
                  </div>
                </div>
              )}
              {selected && (
                <MarksGrid
                  key={selected.course_structure_id}
                  scope={{
                    session: selected.session_id || "",
                    class: selected.class_id || "",
                    section: selected.section_id || "",
                    breakdown: selected.breakdown_id || "",
                  }}
                  breakdownTitle={selected.breakdown_title}
                  classNameText={selected.class_name}
                  sectionNameText={selected.section_name}
                  subject={{
                    courseStructureId: selected.course_structure_id,
                    course: { _id: selected.subject_id || "", title: selected.subject_name },
                    totalMarks: selected.total_marks,
                    allowSum: false,
                    subCategories: [],
                  }}
                  onClose={() => setSelected(null)}
                  isTeacherView
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Admin cascade view ──────────────────────────────────────────────
const AdminMarksEntry = () => {
  const currentBranch = useSelector(selectCurrentBranch)
  const allBranches = useSelector(selectBranches)

  const [sessions, setSessions] = useState<Session[]>([])
  const [terms, setTerms] = useState<Term[]>([])
  const [breakdowns, setBreakdowns] = useState<ResultBreakdown[]>([])
  const [classes, setClasses] = useState<ClassOpt[]>([])

  // Cascade order (verbatim RMS): Session → Campus → Exam term → Breakdown → Class → Group(if any) → Section(s)
  const [filter, setFilter] = useState({
    sessionTitle: "",
    campus: "",
    term: "",
    breakdown: "",
    class: "",
    classGroup: "",
  })
  const [allGroups, setAllGroups] = useState<GroupOpt[]>([])
  const [selectedSections, setSelectedSections] = useState<string[]>([])
  const [sectionSubjects, setSectionSubjects] = useState<Record<string, MarksSubject[]>>({})
  const [loadingSections, setLoadingSections] = useState<Record<string, boolean>>({})
  const [openCard, setOpenCard] = useState<{ sectionId: string; subject: MarksSubject } | null>(null)
  const gridRef = useRef<HTMLDivElement>(null)

  // Initialize campus to current active branch when available
  useEffect(() => {
    if (currentBranch?._id && !filter.campus) {
      setFilter(prev => ({ ...prev, campus: currentBranch._id }))
    }
  }, [currentBranch?._id]) // eslint-disable-line react-hooks/exhaustive-deps

  // Load all sessions across campuses with all: true
  useEffect(() => {
    getAllSessions({ all: true, limit: 1000 })
      .then((r: any) => setSessions(r?.sessions || []))
      .catch(() => setSessions([]))
  }, [])

  // Deduplicate sessions by title for the dropdown
  const uniqueSessionsByTitle = useMemo(() => {
    const seen = new Set<string>()
    const out: Session[] = []
    for (const s of sessions) {
      if (s.title && !seen.has(s.title)) {
        seen.add(s.title)
        out.push(s)
      }
    }
    return out
  }, [sessions])

  // Dynamically resolve session ID for the selected campus and session title
  const resolvedSession = useMemo(() => {
    if (!filter.sessionTitle) return null
    if (filter.campus) {
      const match = sessions.find(s => {
        const bId = typeof s.branch === "object" && s.branch ? (s.branch as any)._id : s.branch
        return s.title === filter.sessionTitle && String(bId) === filter.campus
      })
      if (match) return match
    }
    return sessions.find(s => s.title === filter.sessionTitle) || null
  }, [sessions, filter.sessionTitle, filter.campus])

  const resolvedSessionId = resolvedSession?._id || ""

  // Load exam terms once
  useEffect(() => {
    getAllTerms(1, 100)
      .then((r: any) => setTerms(r?.data || []))
      .catch(() => setTerms([]))
  }, [])

  // Load groups once (a class split into streams, e.g. Biology/Computer).
  // Most classes have none, so this is cheap to fetch up front rather than
  // re-querying per class.
  useEffect(() => {
    listGroups()
      .then((r: any) =>
        setAllGroups(
          ((r?.data || []) as any[]).map(g => ({
            _id: g._id,
            name: g.name,
            system: g.system?._id || g.system,
            classes: (g.classes || []).map((c: any) => c?._id || c),
          }))
        )
      )
      .catch(() => setAllGroups([]))
  }, [])

  // Load classes per session and campus so section documents are properly scoped
  useEffect(() => {
    if (!resolvedSessionId || !filter.campus) { setClasses([]); return }
    getAllClasses({ limit: 500, session: resolvedSessionId, branch: filter.campus, all: "true" } as any)
      .then((r: any) =>
        setClasses(
          (r?.classes || []).map((c: any) => ({
            _id: c._id,
            name: c.name,
            sections: (c.sections || []).filter(Boolean),
            system: c.system?._id || c.system,
          }))
        )
      )
      .catch(() => setClasses([]))
  }, [resolvedSessionId, filter.campus])

  // Load breakdowns when session and campus are selected
  // Bypasses the active-only status restriction so draft/submitted/active breakdowns all show
  useEffect(() => {
    if (!resolvedSessionId || !filter.campus) { setBreakdowns([]); return }
    getAllResultBreakdowns({
      session: resolvedSessionId,
      branchId: filter.campus,
      all: true,
      limit: 500,
    })
      .then(r => setBreakdowns(r?.data || []))
      .catch(() => setBreakdowns([]))
  }, [resolvedSessionId, filter.campus])

  // Cascade reset: clearing an upstream pick clears everything downstream
  const setF = (k: keyof typeof filter, v: string) => {
    setFilter(s => {
      const next = { ...s, [k]: v }
      const order: (keyof typeof filter)[] = ["sessionTitle", "campus", "term", "breakdown", "class", "classGroup"]
      const i = order.indexOf(k)
      if (i >= 0) {
        for (let j = i + 1; j < order.length; j++) next[order[j]] = ""
      }
      return next
    })
    setSelectedSections([])
    setSectionSubjects({})
    setOpenCard(null)
  }

  // Filter breakdowns by picked Exam Term
  const filteredBreakdowns = useMemo(() => {
    return breakdowns.filter(b => {
      if (resolvedSessionId) {
        const bSess = toId((b as any).sessionId || (b as any).session)
        if (bSess && bSess !== resolvedSessionId) return false
      }
      if (filter.campus) {
        const bBranch = toId((b as any).branchId || (b as any).branch)
        if (bBranch && bBranch !== filter.campus) return false
      }
      if (filter.term) {
        const bTerm = toId((b as any).termId || (b as any).term)
        if (bTerm && bTerm !== filter.term) return false
      }
      return true
    })
  }, [breakdowns, resolvedSessionId, filter.campus, filter.term])

  const selectedBreakdown = breakdowns.find(b => b._id === filter.breakdown)

  // Classes offered by the chosen breakdown
  const classOptions: ClassOpt[] = useMemo(() => {
    if (!selectedBreakdown) return []
    const breakdownClasses = (selectedBreakdown.classes || []) as Array<any>
    if (!breakdownClasses.length) return []

    const breakdownClassIds = new Set(breakdownClasses.map(c => toId(c)))
    const matched = classes.filter(c => breakdownClassIds.has(c._id))
    if (matched.length > 0) return matched

    return breakdownClasses.map(c => {
      const id = toId(c)
      const found = classes.find(cl => cl._id === id)
      return {
        _id: id,
        name: (typeof c === "object" && c ? c.name : "") || found?.name || id,
        sections: found?.sections || [],
        system: found?.system,
      }
    })
  }, [selectedBreakdown, classes])

  // Sections for the picked class
  const sectionOptions: SectionOpt[] = useMemo(() => {
    return classOptions.find(c => c._id === filter.class)?.sections || []
  }, [classOptions, filter.class])

  // Groups that actually apply to the selected class -- most classes have
  // none (empty here, dropdown stays hidden). A class split into streams
  // (e.g. Grade IX Science -> Biology/Computer) without this picked stays
  // on the backend's "merge every group's subjects together" default view
  // -- which is exactly the "Computer and Bio combined" bug reported live.
  const selectedClassSystem = classOptions.find(c => c._id === filter.class)?.system
  const groupsForFilter = useMemo(
    () =>
      filter.class
        ? allGroups.filter(g => g.system === selectedClassSystem && g.classes.includes(filter.class))
        : [],
    [allGroups, selectedClassSystem, filter.class]
  )

  const toggleSection = (sectionId: string) => {
    setSelectedSections(s => (s.includes(sectionId) ? s.filter(id => id !== sectionId) : [...s, sectionId]))
    setOpenCard(null)
  }

  const toggleAllSections = () => {
    if (selectedSections.length === sectionOptions.length) {
      setSelectedSections([])
    } else {
      setSelectedSections(sectionOptions.map((s: SectionOpt) => s._id))
    }
    setOpenCard(null)
  }

  // Load subjects for each newly-selected section. If the class is split
  // into groups, a group must be picked first -- omitting it entirely asks
  // the backend for every group's subjects merged together (its documented
  // default view "before a group is chosen"), which is exactly the
  // "Computer and Bio combined" bug reported live.
  useEffect(() => {
    if (!resolvedSessionId || !filter.breakdown || !filter.class) return
    if (groupsForFilter.length > 0 && !filter.classGroup) return
    selectedSections.forEach(sectionId => {
      if (sectionSubjects[sectionId] || loadingSections[sectionId]) return
      setLoadingSections(s => ({ ...s, [sectionId]: true }))
      listMarksSubjects({
        session: resolvedSessionId,
        class: filter.class,
        section: sectionId,
        breakdown: filter.breakdown,
        ...(filter.classGroup ? { classGroup: filter.classGroup } : {}),
      })
        .then(r => setSectionSubjects(s => ({ ...s, [sectionId]: r?.data || [] })))
        .catch((e: any) => {
          toast.error(e?.response?.data?.message || "Failed to load subjects")
          setSectionSubjects(s => ({ ...s, [sectionId]: [] }))
        })
        .finally(() => setLoadingSections(s => ({ ...s, [sectionId]: false })))
    })
  }, [selectedSections, resolvedSessionId, filter.breakdown, filter.class, filter.classGroup, groupsForFilter]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (openCard && gridRef.current) {
      gridRef.current.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }, [openCard])

  const courseName = (c: MarksSubject["course"]) => c?.title || c?.name || "Subject"
  const sectionName = (sectionId: string) => sectionOptions.find((s: SectionOpt) => s._id === sectionId)?.name || ""

  // Flatten (Section × Subject) pairs for the RMS cards grid
  const subjectSectionPairs = useMemo(() => {
    const pairs: Array<{ sectionId: string; subject: MarksSubject }> = []
    selectedSections.forEach(sectionId => {
      const subjs = sectionSubjects[sectionId] || []
      subjs.forEach(subject => {
        pairs.push({ sectionId, subject })
      })
    })
    return pairs
  }, [selectedSections, sectionSubjects])

  // ── Blank mark sheet (Image 4 verbatim RMS parity) ─────────────────
  const [printingBlank, setPrintingBlank] = useState(false)

  const blankSheetCSS = `
    @page { size: A4 portrait; margin: 12mm; }
    * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif; color: #0f172a; font-size: 10pt; margin: 0; }
    .sheet { page-break-after: always; padding: 2mm 0; }
    .sheet:last-child { page-break-after: auto; }
    .header { display: flex; align-items: center; justify-content: space-between; border-bottom: 2px solid #0f172a; padding-bottom: 8px; margin-bottom: 12px; }
    .school { font-size: 16pt; font-weight: 700; letter-spacing: -0.02em; }
    .school small { display: block; font-weight: 400; font-size: 9pt; color: #64748b; }
    .meta { text-align: right; font-size: 9pt; color: #475569; }
    .pill { display: inline-block; background: #0f172a; color: white; padding: 2px 8px; border-radius: 999px; font-size: 9pt; font-weight: 500; letter-spacing: 0.04em; text-transform: uppercase; }
    .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 4px 16px; margin-bottom: 10px; font-size: 9pt; }
    .grid2 strong { color: #475569; font-weight: 500; }
    .examFields { display: flex; flex-wrap: wrap; gap: 6px 24px; margin-bottom: 14px; font-size: 9pt; }
    .examFields strong { color: #475569; font-weight: 500; }
    .fillLine { display: inline-block; border-bottom: 1px solid #94a3b8; min-width: 110px; height: 13px; vertical-align: bottom; }
    table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 9pt; table-layout: fixed; }
    /* table-layout:fixed pins each column's width but does NOT stop long
       unbroken labels (e.g. "Vocabulary & Spellings") from visually
       overflowing past the border into the next cell -- break-word forces
       them to wrap inside their own column instead. */
    th, td { border: 1px solid #cbd5e1; padding: 4px 6px; overflow-wrap: break-word; word-break: break-word; }
    th { background: #f1f5f9; font-weight: 600; text-align: left; }
    td.sr { width: 28px; text-align: center; color: #64748b; }
    td.roll { width: 48px; text-align: center; font-family: ui-monospace, monospace; font-size: 8.5pt; font-weight: 600; }
    td.empty { height: 24px; }
    .footer { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 8px; margin-top: 36px; font-size: 9pt; color: #64748b; }
    .sig { border-top: 1px solid #94a3b8; padding-top: 4px; text-align: center; }
  `

  const esc = (v: unknown) =>
    String(v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;")

  const buildSheetHtml = (
    sectionId: string,
    subject: MarksSubject,
    students: { sNo?: number; rollNo?: string | number; grNo?: string; studentName?: string }[]
  ) => {
    const examTitle = selectedBreakdown?.title || ""
    const currentCampusObj = allBranches.find(b => b._id === filter.campus) || currentBranch
    const campusName = currentCampusObj?.name || ""
    const sessionTitle = filter.sessionTitle || resolvedSession?.title || ""
    const termObj = terms.find(t => t._id === filter.term)
    const termTitle = termObj?.title || ""
    const className = classOptions.find(c => c._id === filter.class)?.name || ""
    const schoolName = currentCampusObj?.schoolName || "Metropolitan Academy"

    const cols = subject.subCategories?.length
      ? subject.subCategories.map(sc => ({ label: sc.description, max: sc.marks }))
      : [{ label: "Marks", max: subject.totalMarks }]

    return `
      <div class="sheet">
        <div class="header">
          <div class="school">
            ${esc(schoolName)}
            <small>A Karachi institution since 1967</small>
          </div>
          <div class="meta">
            <div class="pill">Marks sheet (blank)</div>
            <div style="margin-top:4px"><strong>${esc(examTitle)}</strong></div>
            <div>${esc(sessionTitle)} • ${esc(campusName)}</div>
          </div>
        </div>

        <div class="grid2">
          <div><strong>Subject:</strong> ${esc(courseName(subject.course))}</div>
          <div><strong>Class:</strong> ${esc(className)}${sectionId ? ` — ${esc(sectionName(sectionId))}` : ""}</div>
          <div><strong>Total marks:</strong> ${Number(subject.totalMarks).toFixed(2)}</div>
          <div><strong>Term:</strong> ${esc(termTitle)}</div>
          ${subject.classGroup?.name ? `<div><strong>Group:</strong> ${esc(subject.classGroup.name)}</div>` : ""}
        </div>

        <div class="examFields">
          <div><strong>Name of examiner:</strong> <span class="fillLine"></span></div>
          <div><strong># of students:</strong> <span class="fillLine"></span></div>
          <div><strong>Date:</strong> <span class="fillLine"></span></div>
        </div>

        <table>
          <thead>
            <tr>
              <th style="width:28px">Sr</th>
              <th style="width:48px">Roll</th>
              <th style="width:150px">Name</th>
              ${cols
                .map(
                  c =>
                    `<th style="width:65px;text-align:center">${esc(c.label)}<br/><small style="font-weight:400;color:#64748b">/${Number(c.max).toFixed(0)}</small></th>`
                )
                .join("")}
            </tr>
          </thead>
          <tbody>
            ${students
              .map(
                (s, i) => `<tr>
              <td class="sr">${s.sNo ?? i + 1}</td>
              <td class="roll">${esc(s.rollNo ?? "")}</td>
              <td>${esc(s.studentName ?? "")}</td>
              ${cols.map(() => `<td class="empty"></td>`).join("")}
            </tr>`
              )
              .join("")}
          </tbody>
        </table>

        <div class="footer">
          <div class="sig">Teacher signature</div>
          <div class="sig">Deputy Head</div>
          <div class="sig">Principal</div>
        </div>
      </div>`
  }

  const writeSheetsIntoWindow = (win: Window, sheets: string[]) => {
    win.document.open()
    win.document.write(
      `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Blank Marks Sheets</title><style>${blankSheetCSS}</style></head><body>${sheets.join(
        ""
      )}</body></html>`
    )
    win.document.close()
    let started = false
    const start = () => {
      if (started) return
      started = true
      win.focus()
      win.print()
    }
    win.onafterprint = () => win.close()
    win.onload = start
    setTimeout(start, 600)
  }

  const printBlankFor = async (
    specs: { sectionId: string; subject: MarksSubject }[],
    emptyMessage: string
  ) => {
    if (!specs.length) {
      toast.warning(emptyMessage)
      return
    }

    // Open the tab synchronously, as the very first thing the click does --
    // bulk print awaits one getMarksGrid call per subject before it has
    // anything to show, and by the time that finishes the browser no longer
    // treats it as a direct result of the click, so window.open() reliably
    // gets popup-blocked. Opening a blank tab immediately (still inside the
    // click's "trusted" window) and filling it in once the data is ready
    // avoids that entirely.
    const win = window.open("", "_blank")
    if (!win) {
      toast.error("Popup blocked — allow popups and try again")
      return
    }
    win.document.write(
      `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Blank Marks Sheets</title></head><body style="font-family:sans-serif;color:#64748b;padding:24px">Preparing sheet(s)…</body></html>`
    )

    setPrintingBlank(true)
    try {
      const sheets: string[] = []
      let skipped = 0
      for (const { sectionId, subject } of specs) {
        const gridRes = await getMarksGrid({
          session: resolvedSessionId,
          class: filter.class,
          section: sectionId,
          breakdown: filter.breakdown,
          courseStructureId: subject.courseStructureId,
        })
        const students = gridRes?.data?.students || []
        if (!students.length) {
          skipped++
          continue
        }
        sheets.push(buildSheetHtml(sectionId, subject, students))
      }

      if (!sheets.length) {
        win.close()
        toast.warning(
          skipped
            ? specs.length === 1
              ? `Section "${sectionName(specs[0].sectionId)}" has no students enrolled in this session.`
              : "Nothing to print — the selected section(s) have no students enrolled for this session."
            : emptyMessage
        )
        return
      }
      if (skipped) toast.info(`${skipped} subject(s) skipped — no students enrolled`)
      writeSheetsIntoWindow(win, sheets)
    } catch (e: any) {
      win.close()
      toast.error(e?.response?.data?.message || "Could not build the blank sheet")
    } finally {
      setPrintingBlank(false)
    }
  }

  const printAllBlankSheets = () =>
    printBlankFor(
      subjectSectionPairs,
      "Nothing to print — no subjects configured for the selected sections"
    )

  const printOneSheet = (sectionId: string, subject: MarksSubject) =>
    printBlankFor([{ sectionId, subject }], "Nothing to print for this subject")

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="mb-3 d-flex align-items-start justify-content-between gap-3">
          <div>
            <h3 className="page-title mb-1">Marks Entry</h3>
            <p className="text-muted mb-0">
              Pick session → campus → exam term → breakdown → class, then choose a subject to enter marks.
            </p>
          </div>
        </div>

        {/* ── Cascade filters: Session → Campus → Exam term → Breakdown → Class ── */}
        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-2">
              {/* 1. Session */}
              <div className="col-12 col-sm-6 col-md-4 col-lg">
                <label className="form-label small fw-semibold mb-1">Session</label>
                <select
                  className="form-select form-select-sm"
                  value={filter.sessionTitle}
                  onChange={e => setF("sessionTitle", e.target.value)}
                >
                  <option value="">— Select session —</option>
                  {uniqueSessionsByTitle.map(s => (
                    <option key={s.title} value={s.title}>
                      {s.title}
                    </option>
                  ))}
                </select>
              </div>

              {/* 2. Campus */}
              <div className="col-12 col-sm-6 col-md-4 col-lg">
                <label className="form-label small fw-semibold mb-1">Campus</label>
                <select
                  className="form-select form-select-sm"
                  value={filter.campus}
                  disabled={!filter.sessionTitle}
                  onChange={e => setF("campus", e.target.value)}
                >
                  <option value="">{!filter.sessionTitle ? "Pick session first" : "— Select campus —"}</option>
                  {allBranches.map((b: Branch) => (
                    <option key={b._id} value={b._id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* 3. Exam term */}
              <div className="col-12 col-sm-6 col-md-4 col-lg">
                <label className="form-label small fw-semibold mb-1">Exam term</label>
                <select
                  className="form-select form-select-sm"
                  value={filter.term}
                  disabled={!filter.campus}
                  onChange={e => setF("term", e.target.value)}
                >
                  <option value="">{!filter.campus ? "Pick campus first" : "— Select term —"}</option>
                  {terms.map(t => {
                    const count = breakdowns.filter(b => {
                      const bTerm = toId((b as any).termId || (b as any).term)
                      return bTerm && bTerm === t._id
                    }).length
                    return (
                      <option key={t._id} value={t._id}>
                        {t.title} {count > 0 ? `(${count})` : "(0)"}
                      </option>
                    )
                  })}
                </select>
              </div>

              {/* 4. Breakdown */}
              <div className="col-12 col-sm-6 col-md-4 col-lg">
                <label className="form-label small fw-semibold mb-1">Breakdown</label>
                <select
                  className="form-select form-select-sm"
                  value={filter.breakdown}
                  disabled={!filter.term || filteredBreakdowns.length === 0}
                  onChange={e => setF("breakdown", e.target.value)}
                >
                  <option value="">
                    {!filter.term
                      ? "Pick exam term first"
                      : filteredBreakdowns.length === 0
                      ? "No breakdowns found"
                      : "— Select breakdown —"}
                  </option>
                  {filteredBreakdowns.map(b => (
                    <option key={b._id} value={b._id}>
                      {b.title}
                    </option>
                  ))}
                </select>
              </div>

              {/* 5. Class */}
              <div className="col-12 col-sm-6 col-md-4 col-lg">
                <label className="form-label small fw-semibold mb-1">Class</label>
                <select
                  className="form-select form-select-sm"
                  value={filter.class}
                  disabled={!filter.breakdown || classOptions.length === 0}
                  onChange={e => setF("class", e.target.value)}
                >
                  <option value="">
                    {!filter.breakdown
                      ? "Pick breakdown first"
                      : classOptions.length === 0
                      ? "No classes in breakdown"
                      : "— Select class —"}
                  </option>
                  {classOptions.map(c => (
                    <option key={c._id} value={c._id}>
                      {selectedBreakdown?.system?.name ? `${selectedBreakdown.system.name} — ` : ""}
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* 6. Group -- only rendered once a class with real groups is
                  picked, most classes have none (e.g. Grade IX split into
                  Biology/Computer). Required, not optional, once it shows:
                  omitting it is what caused the two groups' subjects to
                  show combined. */}
              {groupsForFilter.length > 0 && (
                <div className="col-12 col-sm-6 col-md-4 col-lg">
                  <label className="form-label small fw-semibold mb-1">Group</label>
                  <select
                    className="form-select form-select-sm"
                    value={filter.classGroup}
                    onChange={e => setF("classGroup", e.target.value)}
                  >
                    <option value="">— Select group —</option>
                    {groupsForFilter.map(g => (
                      <option key={g._id} value={g._id}>{g.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {groupsForFilter.length > 0 && !filter.classGroup && (
              <div className="alert alert-warning py-2 px-3 small mt-2 mb-0 d-flex align-items-center gap-2">
                <i className="ti ti-alert-triangle text-warning fs-5" />
                <span>
                  This class is split into groups ({groupsForFilter.map(g => g.name).join(", ")}) —
                  pick one above to see its subjects.
                </span>
              </div>
            )}

            {/* Informative alert if picked exam term has no breakdowns */}
            {filter.term && filteredBreakdowns.length === 0 && (
              <div className="alert alert-info py-2 px-3 small mt-2 mb-0 d-flex align-items-center gap-2">
                <i className="ti ti-info-circle text-info fs-5" />
                <span>
                  No result breakdowns have been created for{" "}
                  <strong>{terms.find(t => t._id === filter.term)?.title || "this term"}</strong> at this campus.
                  {breakdowns.length > 0 ? (
                    <>
                      {" "}Existing breakdowns for this campus are in:{" "}
                      <strong>
                        {Array.from(
                          new Set(
                            breakdowns
                              .map(b => {
                                const bTermId = toId((b as any).termId || (b as any).term)
                                return terms.find(t => t._id === bTermId)?.title
                              })
                              .filter(Boolean)
                          )
                        ).join(", ")}
                      </strong>.
                    </>
                  ) : (
                    " No breakdowns found for any term at this campus."
                  )}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* ── Section Checkbox Pills ── */}
        <div className="card mb-3">
          <div className="card-body p-3">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <span className="small fw-semibold text-secondary">
                Sections:{" "}
                {filter.class && sectionOptions.length > 0 ? (
                  <span className="text-dark">
                    {selectedSections.length} of {sectionOptions.length} ticked
                  </span>
                ) : (
                  <span className="text-muted">Pick a class to load sections</span>
                )}
              </span>
              {filter.class && sectionOptions.length > 0 && (
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 text-decoration-none fw-semibold"
                  onClick={toggleAllSections}
                >
                  {selectedSections.length === sectionOptions.length ? "Clear" : "Select all"}
                </button>
              )}
            </div>

            {filter.class && sectionOptions.length > 0 ? (
              <div className="d-flex flex-wrap gap-2">
                {sectionOptions.map((s: SectionOpt) => {
                  const isChecked = selectedSections.includes(s._id)
                  return (
                    <label
                      key={s._id}
                      className={`btn btn-sm mb-0 d-inline-flex align-items-center gap-2 cursor-pointer ${
                        isChecked ? "btn-primary text-white" : "btn-outline-secondary"
                      }`}
                      style={{ borderRadius: "20px", padding: "4px 12px" }}
                    >
                      <input
                        type="checkbox"
                        className="form-check-input m-0 d-none"
                        checked={isChecked}
                        onChange={() => toggleSection(s._id)}
                      />
                      <span>{isChecked ? "✓ " : ""}{s.name}</span>
                    </label>
                  )
                })}
              </div>
            ) : (
              <div className="text-muted small">
                {!filter.class ? "Select class in cascade above." : "No sections defined for this class."}
              </div>
            )}
          </div>
        </div>

        {/* ── Bulk print blank sheets bar (RMS parity) ── */}
        {filter.class && filter.breakdown && selectedSections.length > 0 && (
          <div className="alert alert-light border d-flex flex-wrap align-items-center justify-content-between gap-2 p-2 mb-3">
            <div className="small text-muted">
              <strong className="text-dark">Bulk print blank sheets</strong> — one PDF for every section × subject in
              this class, each on its own page(s).
            </div>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={printAllBlankSheets}
              disabled={printingBlank || subjectSectionPairs.length === 0}
            >
              <i className="ti ti-printer me-1" />
              {printingBlank ? "Generating…" : "Bulk print blank sheets"}
            </button>
          </div>
        )}

        {/* ── Subject × Section Cards Grid (RMS 2-column layout) ── */}
        {!filter.class ? (
          <div className="card">
            <div className="card-body text-center text-muted py-5">
              Walk through the cascade above to load subjects.
            </div>
          </div>
        ) : selectedSections.length === 0 ? (
          <div className="card">
            <div className="card-body text-center text-muted py-5">
              Tick one or more sections above to view subjects.
            </div>
          </div>
        ) : subjectSectionPairs.length === 0 ? (
          <div className="card">
            <div className="card-body text-center text-muted py-5">
              {Object.values(loadingSections).some(Boolean)
                ? "Loading subjects…"
                : "No subjects (course structures) configured for the selected sections."}
            </div>
          </div>
        ) : (
          <div className="row g-2 mb-4">
            {subjectSectionPairs.map(({ sectionId, subject }) => {
              const clsName = classOptions.find(c => c._id === filter.class)?.name || ""
              const secName = sectionName(sectionId)
              const isOpen = openCard?.sectionId === sectionId && openCard?.subject.courseStructureId === subject.courseStructureId

              return (
                <div className="col-12 col-md-6" key={`${subject.courseStructureId}::${sectionId}`}>
                  <div className={`card mb-0 h-100 ${isOpen ? "border-primary shadow-sm" : ""}`}>
                    <div className="card-body p-3 d-flex align-items-center justify-content-between gap-3">
                      <div>
                        <h6 className="mb-0 fw-semibold">
                          {clsName} · {secName} — {courseName(subject.course)}
                        </h6>
                        <small className="text-muted">
                          Total marks {Number(subject.totalMarks).toFixed(2)}
                          {subject.subCategories?.length > 0 && ` · ${subject.subCategories.length} sub-parts`}
                        </small>
                      </div>
                      <div className="d-flex shrink-0 align-items-center gap-2">
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => printOneSheet(sectionId, subject)}
                          disabled={printingBlank}
                        >
                          Print blank sheet
                        </button>
                        <button
                          className={`btn btn-sm ${isOpen ? "btn-primary" : "btn-outline-primary"}`}
                          onClick={() => setOpenCard({ sectionId, subject })}
                        >
                          Open
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* ── Marks entry grid (opened below subject cards, with auto-scroll) ── */}
        {openCard && selectedBreakdown && (
          <div ref={gridRef} className="mt-4">
            <MarksGrid
              key={`${openCard.sectionId}::${openCard.subject.courseStructureId}`}
              scope={{
                session: resolvedSessionId,
                class: filter.class,
                section: openCard.sectionId,
                breakdown: filter.breakdown,
              }}
              breakdownTitle={selectedBreakdown.title}
              classNameText={classOptions.find(c => c._id === filter.class)?.name || ""}
              sectionNameText={sectionName(openCard.sectionId)}
              subject={openCard.subject}
              onClose={() => setOpenCard(null)}
            />
          </div>
        )}
      </div>
    </div>
  )
}

// ── Exam Details Form (Examiner name + Date conducted with Save details button) ──
const ExamMetaForm = ({
  awardSlipId,
  initialExaminer,
  initialDate,
  studentCount,
  disabled,
  onSaved,
}: {
  awardSlipId: string
  initialExaminer: string
  initialDate: string
  studentCount: number
  disabled: boolean
  onSaved: () => void
}) => {
  const [examinerName, setExaminerName] = useState(initialExaminer)
  const [dateConducted, setDateConducted] = useState(initialDate)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setExaminerName(initialExaminer)
    setDateConducted(initialDate)
  }, [initialExaminer, initialDate])

  const dirty =
    examinerName.trim() !== (initialExaminer || "").trim() ||
    (dateConducted || "") !== (initialDate || "")

  const handleSave = async () => {
    setSaving(true)
    try {
      await saveExamMeta({
        awardSlipId,
        examinerName: examinerName.trim(),
        dateConducted: dateConducted || null,
      })
      toast.success("Exam details saved")
      onSaved()
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to save exam details")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="card bg-light border-0 mb-3">
      <div className="card-body p-3">
        <div className="row g-2 align-items-end">
          <div className="col-12 col-md-4">
            <label className="form-label small fw-semibold text-muted mb-1">Examiner name</label>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="e.g. Ms. Sara Khan"
              value={examinerName}
              disabled={disabled}
              onChange={e => setExaminerName(e.target.value)}
            />
          </div>
          <div className="col-12 col-md-3">
            <label className="form-label small fw-semibold text-muted mb-1">Date conducted</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={dateConducted}
              disabled={disabled}
              onChange={e => setDateConducted(e.target.value)}
            />
          </div>
          <div className="col-12 col-md-2">
            <label className="form-label small fw-semibold text-muted mb-1"># of students</label>
            <input type="text" className="form-control form-control-sm" value={studentCount} disabled readOnly />
          </div>
          <div className="col-12 col-md-3">
            <button
              type="button"
              className={`btn btn-sm w-100 ${dirty ? "btn-primary" : "btn-secondary"}`}
              disabled={disabled || saving || !dirty}
              onClick={handleSave}
            >
              {saving ? "Saving…" : dirty ? "Save details" : "Saved"}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Marks entry editor grid ─────────────────────────────────────────
const MarksGrid = ({
  scope,
  breakdownTitle,
  classNameText,
  sectionNameText,
  subject,
  onClose,
  isTeacherView = false,
}: {
  scope: { session: string; class: string; section: string; breakdown: string }
  breakdownTitle: string
  classNameText: string
  sectionNameText: string
  subject: MarksSubject
  onClose: () => void
  // Teacher self-service: submit goes through the ownership-scoped teacher
  // endpoint instead of the admin one, and there's no Unsubmit/exam-meta --
  // those stay admin-only, same as before the pages were merged.
  isTeacherView?: boolean
}) => {
  const [grid, setGrid] = useState<MarksGridData | null>(null)
  const [loading, setLoading] = useState(true)
  const [edits, setEdits] = useState<Record<string, string>>({}) // key `${enrollmentId}::${subId|__term__}`
  const [saving, setSaving] = useState(false)
  const [busy, setBusy] = useState(false)
  const [confirm, setConfirm] = useState<ConfirmState | null>(null)

  const load = () => {
    setLoading(true)
    getMarksGrid({ ...scope, courseStructureId: subject.courseStructureId })
      .then(r => {
        setGrid(r.data)
        setEdits({})
      })
      .catch((e: any) => toast.error(e?.response?.data?.message || "Failed to load grid"))
      .finally(() => setLoading(false))
  }

  useEffect(load, [subject.courseStructureId]) // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="card">
        <div className="card-body text-center text-muted py-4">Loading marks grid…</div>
      </div>
    )
  }
  if (!grid) return null

  const subs = grid.subCategories || []
  const hasSubs = subs.length > 0
  const maxBySub: Record<string, number> = Object.fromEntries(subs.map(s => [s._id, Number(s.marks)]))
  const locked = grid.locked
  const editLocked = grid.locked || grid.submitted

  const cellKey = (eid: string, subId: string | null) => `${eid}::${subId || "__term__"}`

  const cellValue = (eid: string, subId: string | null) => {
    const k = cellKey(eid, subId)
    if (k in edits) return edits[k]
    const cell = grid.students.find(s => s.enrollmentId === eid)?.cells[subId || "__term__"]
    if (!cell) return ""
    if (cell.isNa) return "NA"
    if (cell.isAbsent) return "AB"
    return cell.obtainedMarks != null ? String(cell.obtainedMarks) : ""
  }

  const setCell = (eid: string, subId: string | null, v: string) => {
    let norm = v
    if (isNaMarker(v)) norm = "NA"
    else if (isAbsentMarker(v)) norm = "AB"
    setEdits(e => ({ ...e, [cellKey(eid, subId)]: norm }))
  }

  const outOfRange = (subId: string | null, v: any) => {
    if (v === "" || v == null) return false
    if (isAbsentMarker(v) || isNaMarker(v)) return false
    const n = Number(v)
    if (Number.isNaN(n)) return true
    const max = subId ? maxBySub[subId] : Number(grid.totalMarks)
    return n < 0 || (max != null && n > max)
  }

  const save = async () => {
    const entries: SaveMarksEntry[] = Object.entries(edits).map(([k, v]) => {
      const [enrollmentId, sc] = k.split("::")
      const subCategory = sc === "__term__" ? null : sc
      if (isNaMarker(v)) return { enrollmentId, subCategory, obtainedMarks: null, isAbsent: false, isNa: true }
      if (isAbsentMarker(v)) return { enrollmentId, subCategory, obtainedMarks: null, isAbsent: true, isNa: false }
      const num = v === "" ? null : Number(v)
      return {
        enrollmentId,
        subCategory,
        obtainedMarks: Number.isNaN(num as number) ? null : num,
        isAbsent: false,
        isNa: false,
      }
    })
    if (!entries.length) return
    setSaving(true)
    try {
      const res = await saveMarksGrid({ awardSlipId: grid.awardSlipId, entries })
      toast.success(res.message || "Marks saved")
      load()
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Save failed")
    } finally {
      setSaving(false)
    }
  }

  const doSubmit = async () => {
    setBusy(true)
    try {
      if (isTeacherView) {
        await submitTeacherMarks(grid.awardSlipId)
      } else {
        await submitMarksGrid({
          awardSlipId: grid.awardSlipId,
          examinerName: grid.examinerName,
          dateConducted: grid.dateConducted || undefined,
        })
      }
      toast.success("Marks submitted")
      load()
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Submit failed")
    } finally {
      setBusy(false)
    }
  }

  const doUnsubmit = async () => {
    setBusy(true)
    try {
      await unsubmitMarksGrid(grid.awardSlipId)
      toast.success("Marks unsubmitted — reopened for editing")
      load()
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Unsubmit failed")
    } finally {
      setBusy(false)
    }
  }

  const unsavedCount = Object.keys(edits).length

  return (
    <div className="card shadow-sm border">
      <ConfirmModal confirm={confirm} onClose={() => setConfirm(null)} />

      {/* Header bar: Title, Scope, Back button */}
      <div className="card-header bg-white py-3 d-flex align-items-center justify-content-between flex-wrap gap-2">
        <div>
          <h5 className="mb-0 fw-bold">{subject.course?.title || subject.course?.name || "Subject"}</h5>
          <small className="text-muted">
            {breakdownTitle} · {classNameText} · Section {sectionNameText}
          </small>
        </div>
        <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>
          ← Back to subjects
        </button>
      </div>

      <div className="card-body p-3">
        {/* Status alerts */}
        {grid.submitted && !isTeacherView && (
          <div className="alert alert-success border-success py-2 px-3 mb-3 d-flex align-items-center justify-content-between flex-wrap gap-2">
            <span className="small">
              ✓ Submitted by <strong>{grid.submittedByName || "Staff"}</strong> on{" "}
              {grid.submittedAt ? new Date(grid.submittedAt).toLocaleString() : "—"}. Marks are locked for editing.
            </span>
            <button
              className="btn btn-sm btn-warning text-dark fw-semibold"
              disabled={busy}
              onClick={() =>
                setConfirm({
                  title: "Unsubmit marks",
                  message: "Unsubmit these marks? The teacher will be able to edit and re-submit.",
                  confirmLabel: "Unsubmit",
                  onConfirm: doUnsubmit,
                })
              }
            >
              Unsubmit
            </button>
          </div>
        )}
        {grid.submitted && isTeacherView && (
          <div className="alert alert-warning py-2 px-3 mb-3 small">
            Marks {grid.status} — contact admin to unlock for editing.
          </div>
        )}
        {locked && (
          <div className="alert alert-warning py-2 px-3 mb-3 small">
            This award slip is {grid.status}; marks are permanently locked.
          </div>
        )}

        {/* Examiner details form -- admin only, same as before the pages merged */}
        {!isTeacherView && (
          <ExamMetaForm
            awardSlipId={grid.awardSlipId}
            initialExaminer={grid.examinerName || ""}
            initialDate={grid.dateConducted ? grid.dateConducted.slice(0, 10) : ""}
            studentCount={grid.students.length}
            disabled={editLocked}
            onSaved={load}
          />
        )}

        {/* Actions header */}
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <span className="small text-muted fw-semibold">
            {grid.students.length} students · Total marks {Number(grid.totalMarks).toFixed(2)}
          </span>
          <div className="d-flex align-items-center gap-2">
            {unsavedCount > 0 && <span className="badge bg-warning text-dark">{unsavedCount} unsaved</span>}
            <button
              className="btn btn-sm btn-primary"
              onClick={save}
              disabled={saving || editLocked || unsavedCount === 0}
            >
              {saving ? "Saving…" : "Save"}
            </button>
            {!grid.submitted && !locked && (
              <button
                className="btn btn-sm btn-outline-primary"
                disabled={busy || unsavedCount > 0}
                onClick={() =>
                  setConfirm({
                    title: "Submit marks",
                    message: "Submit these marks? The editor will be locked until admin unsubmits.",
                    confirmLabel: "Submit",
                    onConfirm: doSubmit,
                  })
                }
              >
                Submit
              </button>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="table-responsive border rounded">
          <table className="table table-sm table-hover align-middle mb-0" style={{ fontSize: "13px" }}>
            <thead className="table-light">
              <tr>
                <th style={{ width: "50px", position: "sticky", left: 0, zIndex: 2, background: "#f8f9fa" }}>Roll</th>
                <th style={{ width: "80px", position: "sticky", left: "50px", zIndex: 2, background: "#f8f9fa" }}>GR</th>
                <th style={{ minWidth: "160px", position: "sticky", left: "130px", zIndex: 2, background: "#f8f9fa" }}>Name</th>
                {hasSubs ? (
                  subs.map(sc => (
                    <th key={sc._id} className="text-center" style={{ minWidth: "90px" }}>
                      {sc.description}{" "}
                      <span className="text-muted fw-normal small">/{Number(sc.marks)}</span>
                    </th>
                  ))
                ) : (
                  <th className="text-center" style={{ minWidth: "90px" }}>Marks</th>
                )}
                <th className="text-center fw-bold" style={{ width: "80px" }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {grid.students.length === 0 ? (
                <tr>
                  <td colSpan={4 + (hasSubs ? subs.length : 1)} className="text-center py-5 text-muted">
                    <div className="py-2">
                      <i className="ti ti-users-minus fs-1 text-muted d-block mb-2 opacity-50" />
                      <p className="fw-semibold text-secondary mb-1">No students enrolled</p>
                      <small className="text-muted">
                        Section &quot;{sectionNameText}&quot; currently has 0 students enrolled for this session.
                      </small>
                    </div>
                  </td>
                </tr>
              ) : (
                grid.students.map(s => {
                let rowTotal = 0
                let rowAbsent = false
                let liveCellCount = 0

                return (
                  <tr key={s.enrollmentId}>
                    <td style={{ position: "sticky", left: 0, background: "#fff", fontWeight: 600 }}>
                      {s.rollNo || "—"}
                    </td>
                    <td className="text-muted small" style={{ position: "sticky", left: "50px", background: "#fff" }}>
                      {s.grNo || "—"}
                    </td>
                    <td className="text-nowrap" style={{ position: "sticky", left: "130px", background: "#fff" }}>
                      {s.studentName || "—"}
                    </td>

                    {hasSubs ? (
                      subs.map(sc => {
                        const val = cellValue(s.enrollmentId, sc._id)
                        const absent = isAbsentMarker(val)
                        const na = isNaMarker(val)
                        const oor = outOfRange(sc._id, val)
                        const num = Number(val)

                        if (!na) {
                          liveCellCount += 1
                          if (absent) rowAbsent = true
                          else if (!Number.isNaN(num)) rowTotal += num
                        }

                        return (
                          <td key={sc._id} className="text-center p-1">
                            <input
                              type="text"
                              inputMode="decimal"
                              disabled={editLocked}
                              value={val}
                              onChange={e => setCell(s.enrollmentId, sc._id, e.target.value)}
                              placeholder="0 / A / NA"
                              className={`form-control form-control-sm text-center mx-auto ${
                                na
                                  ? "bg-light text-muted fw-semibold"
                                  : absent
                                  ? "bg-warning-subtle text-warning fw-semibold border-warning"
                                  : oor
                                  ? "border-danger text-danger bg-danger-subtle"
                                  : ""
                              }`}
                              style={{ width: "75px" }}
                            />
                          </td>
                        )
                      })
                    ) : (
                      (() => {
                        const val = cellValue(s.enrollmentId, null)
                        const absent = isAbsentMarker(val)
                        const na = isNaMarker(val)
                        const oor = outOfRange(null, val)
                        const num = Number(val)

                        if (!na) {
                          liveCellCount += 1
                          if (absent) rowAbsent = true
                          else if (!Number.isNaN(num)) rowTotal += num
                        }

                        return (
                          <td className="text-center p-1">
                            <input
                              type="text"
                              inputMode="decimal"
                              disabled={editLocked}
                              value={val}
                              onChange={e => setCell(s.enrollmentId, null, e.target.value)}
                              placeholder="0 / A / NA"
                              className={`form-control form-control-sm text-center mx-auto ${
                                na
                                  ? "bg-light text-muted fw-semibold"
                                  : absent
                                  ? "bg-warning-subtle text-warning fw-semibold border-warning"
                                  : oor
                                  ? "border-danger text-danger bg-danger-subtle"
                                  : ""
                              }`}
                              style={{ width: "85px" }}
                            />
                          </td>
                        )
                      })()
                    )}

                    <td className="text-center fw-bold">
                      {liveCellCount === 0 && (hasSubs || isNaMarker(cellValue(s.enrollmentId, null)))
                        ? "NA"
                        : rowAbsent
                        ? "AB"
                        : hasSubs
                        ? rowTotal.toFixed(2)
                        : "—"}
                    </td>
                  </tr>
                )
              }))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default MarksEntry
